import moment from 'moment'

export const maxMatchable = group => Math.max(0, Math.min(group?.quantity ?? 0, group?.match_candidates ?? 0))

// 'none' is exactly "nothing can be matched", so the pill and the disabled Emparejar button
// cannot disagree: both read maxMatchable.
export const candidatesTone = group => {
  if (maxMatchable(group) === 0) return 'none'
  return (group?.match_candidates ?? 0) >= (group?.quantity ?? 0) ? 'full' : 'partial'
}

// Pieces inside a declaration are fungible; the server pairs arbitrarily, so "the first n" is the whole choice.
export const unitIdsForMatch = (group, n) => [...(group?.stock_unit_ids || [])].sort((a, b) => a - b).slice(0, n)

const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0)

// The awaiting read has no ORDER BY by contract; the consumer sorts.
export const sortAwaitingGroups = groups =>
  [...(groups || [])].sort(
    (a, b) => cmp(a.location_code || '', b.location_code || '') || cmp(a.product_id, b.product_id) || cmp(a.status || '', b.status || ''),
  )

// Forward-compatible with the platform serializer enrichment (CUBBO-2947 follow-up 3): when a group or
// awaiting row already carries product_name / product_skus / store_name, no per-id lookup is needed.
export const summaryFromPayload = row => {
  if (!row || !row.product_name) return null
  return { id: row.product_id, name: row.product_name, skus: Array.isArray(row.product_skus) ? row.product_skus : [], storeName: row.store_name || null }
}

// One lot, named the way the operator's other screens name it. Three cases, in order: no lot at all;
// a real printed code (what Localizaciones shows); and the id as a last resort — which is what a
// platform that has not shipped lot_code yet still sends, so this stays correct mid-deploy.
// `fallback` marks that last case so the cell can mute it, the way ProductCell mutes its own.
export const lotLabel = (row, i18n) => {
  if (!row || row.lot_id == null) return { code: i18n.t('found_stock.queue.no_lot'), expires: null, fallback: false }
  // The code and the expiration are independent facts: lots.unique_code is nullable, and a lot we
  // cannot name can still carry a real expiry the operator needs. Resolve them separately.
  const code = String(row.lot_code ?? '').trim()
  // Strict date-only parse: the platform sends YYYY-MM-DD. A loose moment() would render a datetime
  // one day early in MX/BR and fall back to Date() with a deprecation warning for anything else.
  // No line beats a wrong line.
  const expires = row.lot_expiration_date ? moment(row.lot_expiration_date, 'YYYY-MM-DD', true) : null
  return {
    code: code || i18n.t('found_stock.queue.lot_value', { id: row.lot_id }),
    expires: expires?.isValid() ? expires.format('DD/MM/YYYY') : null,
    fallback: !code,
  }
}

const toHex = bytes => [...bytes].map(b => b.toString(16).padStart(2, '0')).join('')

// RFC 4122 v4. crypto.randomUUID is absent in jsdom and some kiosk browsers, so getRandomValues is
// the first fallback; Math.random is the last resort only, because this id is the correction's
// idempotency identity and a predictable one can replay somebody else's intent.
export const newRequestId = () => {
  const webCrypto = typeof crypto !== 'undefined' ? crypto : undefined
  if (typeof webCrypto?.randomUUID === 'function') return webCrypto.randomUUID()
  if (typeof webCrypto?.getRandomValues === 'function') {
    const bytes = webCrypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40 // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80 // variant 10xx
    const h = toHex(bytes)
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}
